/**
 * `/admin/auth/me` permissions로 사이드바 항목을 걸러낸다.
 * full-access(ROLE_MASTER/ROLE_MANAGER)는 전체를 그대로 반환한다.
 */
import type { MenuPermission } from '@/entities/auth/api/auth.api';
import { hasMenuPermission } from '@/shared/utils/authPermissions';
import { isFullAccessRole } from '@/shared/utils/authRoles';
import type { MenuLink, SidebarMenuItem } from './sidebarMenuConfig';

const isLinkVisible = (
  link: MenuLink,
  permissions: MenuPermission[] | undefined | null,
): boolean => {
  if (!link.menuCode) return true;
  return hasMenuPermission(permissions, link.menuCode, 'READ');
};

export const filterSidebarByPermission = (
  items: SidebarMenuItem[],
  permissions: MenuPermission[] | undefined | null,
  role?: string | null,
): SidebarMenuItem[] => {
  if (isFullAccessRole(role)) {
    return items;
  }

  const filtered: SidebarMenuItem[] = [];

  for (const item of items) {
    if ('type' in item && item.type === 'divider') {
      // 앞뒤가 비는 divider는 아래에서 정리
      filtered.push(item);
      continue;
    }

    if ('children' in item) {
      const children = item.children.filter((child) =>
        isLinkVisible(child, permissions),
      );
      if (children.length > 0) {
        filtered.push({ ...item, children });
      }
      continue;
    }

    if ('key' in item && isLinkVisible(item, permissions)) {
      filtered.push(item);
    }
  }

  // 연속·끝단 divider 정리
  const compact: SidebarMenuItem[] = [];
  for (const item of filtered) {
    const isDivider = 'type' in item && item.type === 'divider';
    if (isDivider) {
      if (compact.length === 0) continue;
      const prev = compact[compact.length - 1];
      if (prev && 'type' in prev && prev.type === 'divider') continue;
      compact.push(item);
      continue;
    }
    compact.push(item);
  }
  while (
    compact.length > 0 &&
    'type' in compact[compact.length - 1]! &&
    (compact[compact.length - 1] as { type: string }).type === 'divider'
  ) {
    compact.pop();
  }

  return compact;
};
